import type { State, UseDocumentResult } from '@ztechapps/common.hook'
import { useObjectState, useQueryEffect, useUserTimeZone } from '@ztechapps/common.hook'
import type { ShippingOptions } from '../types'
import type { Order, ShippingPackage, ShippingWeight } from '@ztechapps/common.contract'
import { OrderDeliveryConfirmationEnum } from '@ztechapps/common.contract'
import {
  computeDefaultInsuredAmount,
  normalizeShippingDate,
  OrderCheckoutActivePanelEnum,
} from '@ztechapps/order.common'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useCallback, useEffect, useRef } from 'react'
import { ounceToPounds } from '@ztechapps/common.utils'
import map from 'lodash/map'

// The shape `buildDefaultShippingPackage` and `normalizeExistingShippingPackages` actually read —
// satisfied by both `Order` and `Logistic`, so the logistics item summary panel builds its own
// default/normalized packages from these same functions instead of re-implementing them.
export type ShippingPackageSourceDocument = {
  totalWeight?: number
  shipping?: { packages?: ShippingPackage[] }
}

export const buildDefaultShippingPackage = (
  document?: ShippingPackageSourceDocument,
  insuredAmount = 100
): ShippingPackage => {
  return {
    weight: { value: ounceToPounds(document?.totalWeight ?? 0, 2), unit: 'pound' },
    dimensions: { unit: 'inch', width: 0, height: 0, length: 0 },
    insured_value: { amount: insuredAmount, currency: 'USD' },
    package_code: 'custom',
    packageType: 'custom',
    label_messages: {
      reference1: '',
    },
  } as ShippingPackage
}

// Normalizes a document's already-saved shipping packages (a stray ounce weight to pounds, a
// legacy 'package' code to 'custom', a missing currency) against a fallback package's dimensions.
// Shared by the order checkout and the logistics shipping-package editor — the only thing that
// differs between them is how they derive the *fallback* package when none are saved yet (order
// floors the insured value at the order's gross; logistics has no gross to floor against), so that
// derivation stays local to each caller.
export const normalizeExistingShippingPackages = (
  existingShippingPackages: ShippingPackage[],
  fallbackPackage?: ShippingPackage
): ShippingPackage[] => {
  return map(existingShippingPackages, (item) => {
    const weight = (
      item?.weight?.unit === 'ounce'
        ? {
            value: ounceToPounds(item?.weight?.value ?? 0, 2),
            unit: 'pound',
          }
        : item?.weight
    ) as ShippingWeight
    const package_code = item?.package_code === 'package' ? 'custom' : (item?.package_code ?? item?.packageType)

    const insured_value = { ...item?.insured_value, currency: 'USD' }

    return {
      ...item,
      dimensions: item?.dimensions ?? fallbackPackage?.dimensions,
      package_code,
      weight,
      insured_value,
      packageType: item?.packageType ?? 'custom',
    }
  })
}

export const mapShippingPackagesFromDocument = (
  document?: Order,
  defaultPackage?: ShippingPackage
): ShippingPackage[] => {
  const fallbackPackage = defaultPackage ?? buildDefaultShippingPackage(document)
  const existingShippingPackages: ShippingPackage[] = document?.shipping?.packages

  if (!isEmpty(existingShippingPackages)) {
    return normalizeExistingShippingPackages(existingShippingPackages, fallbackPackage)
  }

  return [
    {
      ...fallbackPackage,
      insured_value: { ...fallbackPackage?.insured_value, amount: computeDefaultInsuredAmount(document?.gross) },
    },
  ]
}

export const useShippingOptions = ({
  orderDocument,
  activePanel,
  onPackagesReseed,
}: {
  orderDocument: UseDocumentResult<Order>
  activePanel: OrderCheckoutActivePanelEnum
  onPackagesReseed?: () => void
}): State<ShippingOptions> => {
  const [shippingOptions, setShippingOptions] = useObjectState<ShippingOptions>({})

  const timeZone = useUserTimeZone()

  // Keyed on the order id, not a bare boolean, so a different order swapped into the same hook
  // instance re-seeds instead of the latch going stale -- see CreateReturn/Context.tsx's
  // `seededFromOrderRef` and BillToCustomerForm.tsx's `lastLoadedCompanyIdRef` for the same shape.
  const seededInsuredAmountOrderIdRef = useRef<string | undefined>(undefined)

  useQueryEffect(() => {
    const { document } = orderDocument

    //setup the shipping packages if is available
    const defaultPackage = buildDefaultShippingPackage(document)
    const newShippingPackages = mapShippingPackagesFromDocument(document, defaultPackage)

    let billTo = {
      isBillToCustomer: document?.shipping?.billToCustomer ?? false,
      account: document?.shipping?.billToAccount,
      postalCode: document?.shipping?.billToPostalCode,
      countryCode: document?.shipping?.billToCountryCode,
    }

    if (
      !isEmpty(document?.by?.ups?.account?.trim?.()) &&
      !isEmpty(document?.by?.ups?.postalCode?.trim?.()) &&
      !isEmpty(document?.by?.ups?.countryCode) &&
      //activePanel === OrderCheckoutActivePanelEnum.shippingRate &&
      (isEmpty(document?.shipping?.billToAccount) ||
        isEmpty(document?.shipping?.billToPostalCode) ||
        isEmpty(document?.shipping?.billToCountryCode))
    ) {
      billTo = {
        isBillToCustomer: true,
        account: document?.by?.ups?.account?.toUpperCase?.(),
        postalCode: document?.by?.ups?.postalCode?.toUpperCase?.(),
        countryCode: document?.by?.ups?.countryCode?.toUpperCase?.() ?? 'US',
      }
    }

    setValue({
      companyName: document?.by?.company?.name,
      name: isEmpty(document?.shippingAddress?.name) ? document?.by?.profile?.name : document?.shippingAddress?.name,
      email: document?.by?.profile?.email,
      phone: isEmpty(document?.shippingAddress?.phone) ? document?.by?.phone : document?.shippingAddress?.phone,
      customerPoNumber: document?.customerPoNumber,
      deliveryConfirmation: document?.shipping?.deliveryConfirmation ?? OrderDeliveryConfirmationEnum.none,
      shipmentDate: normalizeShippingDate(document?.shipping?.date, timeZone),
      shippingAddress: document?.shippingAddress,
      carrierRate: document?.shipping?.rate ?? null,
      shippingFee: document?.shippingFee ?? 0,
      discount: document?.discount ?? 0,
      taxAmount: document?.taxAmount ?? 0,
      gross: document?.gross ?? 0,
      isManualShippingRate: document?.isManualShippingRate ?? false,
      shippingBillTo: billTo,
      totalWeight: document?.totalWeight ?? 0,
      packages: newShippingPackages,
      defaultPackage,
    } as ShippingOptions)
    onPackagesReseed?.()
  }, orderDocument)

  //custom logic when setting value for the shipping options
  const setValue = useCallback(
    (value: Partial<ShippingOptions>) => {
      if (value?.shippingBillTo?.isBillToCustomer || shippingOptions?.shippingBillTo?.isBillToCustomer) {
        value.shippingFee = 0
      }

      setShippingOptions(value)
    },
    [setShippingOptions, shippingOptions]
  )

  useEffect(() => {
    if (
      seededInsuredAmountOrderIdRef.current === orderDocument?.document?._id ||
      activePanel !== OrderCheckoutActivePanelEnum.shippingOptions ||
      orderDocument.loading ||
      isNil(orderDocument?.document?.gross)
    ) {
      return
    }

    seededInsuredAmountOrderIdRef.current = orderDocument?.document?._id
    setValue({ insuredAmount: computeDefaultInsuredAmount(orderDocument?.document?.gross) })
  }, [activePanel, orderDocument?.loading, orderDocument?.document?._id, orderDocument?.document?.gross, setValue])

  return [shippingOptions, setValue]
}
